import { motion } from "framer-motion";
import { Reveal } from "../components/Motion";
import { PageHero } from "../components/PageHero";
import { CTASection } from "../components/CTASection";
import { Seo } from "../components/Seo";

const TEAM = "https://images.unsplash.com/photo-1608845920884-3c88800feebc?crop=entropy&cs=srgb&fm=jpg&fit=crop&w=1000&h=1150&q=80&ixlib=rb-4.1.0";
const INTERIOR = "https://images.unsplash.com/photo-1630699144919-681cf308ae82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwaW50ZXJpb3IlMjBlbXB0eSUyMHJvb218ZW58MHx8fHwxNzg0Nzc3OTI1fDA&ixlib=rb-4.1.0&q=85";

const values = [
  { t: "Owner-first", d: "Your net return guides every recommendation we make." },
  { t: "Radically transparent", d: "Real-time reporting and itemized statements — always." },
  { t: "Locally rooted", d: "A decade of on-the-ground Massachusetts market knowledge." },
  { t: "Genuinely responsive", d: "A real person who picks up, same day, every day." },
];

export default function About() {
  return (
    <>
      <Seo title="About" description="A decade of residential property management experience across Massachusetts, built on transparency and owner-first service." />
      <PageHero
        overline="Est. 2015 · Massachusetts"
        titleLines={["A decade of", "doing it right."]}
        subtitle="Cummings & Co. was founded on a simple belief: property ownership should feel effortless. Created by seasoned, real estate professionals — because trusted property management shouldn't be hard to find."
      />

      <section className="border-b border-border">
        <div className="mx-auto grid max-w-[1400px] gap-px border-x border-border bg-border lg:grid-cols-2">
          <div className="relative min-h-[420px] overflow-hidden bg-background">
            <motion.img initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              src={TEAM} alt="Brick residential building in Boston, Massachusetts" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="bg-background p-8 lg:p-16">
            <span className="overline text-accent">Our approach</span>
            <h2 className="mt-5 font-serif text-3xl font-light leading-tight lg:text-4xl">
              Transparent Pricing, No Surprises
            </h2>
            <div className="mt-6 space-y-5 text-muted-foreground">
              <p>Unlike traditional property management firms, our fee structure is refreshingly simple. No setup fees. No tenant placement fees. No contract renewal fees. Just one flat percentage of monthly rent — that's it.</p>
              <p>The result: owners save approximately 30% annually compared to competitors, with complete clarity on what they're paying for.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-28">
          <Reveal><span className="overline text-accent">What we stand for</span></Reveal>
          <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 0.08} className="bg-background p-8 lg:p-10">
                <span className="font-serif text-4xl font-light text-accent">0{i + 1}</span>
                <h3 className="mt-6 font-serif text-2xl font-normal">{v.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-b border-border">
        <img src={INTERIOR} alt="Managed interior" className="h-[380px] w-full object-cover lg:h-[460px]" />
        <div className="absolute inset-0 flex items-center bg-primary/60">
          <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-10">
            <p className="max-w-3xl font-serif text-3xl font-light leading-tight text-primary-foreground lg:text-5xl">
              "We don't just manage properties. We protect the reason you invested in the first place."
            </p>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
